/**
 * @file keyboard-lighting.ts
 * @description GCOME_AP 磁轴键盘灯光功能（领域驱动实现）。
 *
 * 遵循 KeyboardLightingFeature 规范：
 * - 只暴露合同方法给 Web 端，细节（侧灯亮度/速度/颜色等）通过领域 CRUD
 *   getConfig / setConfig / setProperty / getProperty 管理；
 * - 以 LightingDomainType 领域（Static / Dynamic / Side）组织配置；
 * - 协议细节常量（功能变量区偏移、模式编码等）封装在模块内部，不对外导出。
 *
 * 灯效控制通过 GetFunc/SetFunc（CMD=0x05/0x06）读写 64 字节功能变量区，
 * 自定义灯光（GAME_MODE=0 逐键 RGB）通过 GetLedDefine/SetLedDefine（0x0a/0x0b），
 * 背光/侧灯"关闭前模式"持久化到 APP 自行管理的数据区（0xf1/0xf2）。
 */

import { KeyboardLightingFeature } from '../../../../features/keyboard-lighting/contract'
import { LightingDomainRegistry } from '../../../../features/keyboard-lighting/LightingDomainRegistry'
import type { GcomeApKeyboardDevice } from '../../../../devices/keyboard/gcome-ap/device'
import { GCOME_AP_CMD } from '../../protocols/cmd'
import * as shared from '../../../shared/feature-impls/keyboard-lighting/lighting'
import {
  FUNC_OFFSET,
  SIDE_LIGHT_MODE,
  LIGHT_MODULE,
  LIGHT_MODE,
  KEY_COUNT,
  LIGHT_STATE_APP_OFFSET,
  LIGHT_STATE_UNSET,
} from '../../protocols/lighting'
import {
  getActiveProfile as capGetActiveProfile,
  readFuncBlock as capReadFuncBlock,
  mutateFuncBlock as capMutateFuncBlock,
  readLedDefine as capReadLedDefine,
  writeLedDefine as capWriteLedDefine,
  readKeyMatrix as capReadKeyMatrix,
  gcomeSendRead,
  gcomeSendWrite,
} from '../../utils/lightingCapabilities'
import type {
  GcomeApKeyMatrixEntry,
  KeyboardLightingEffect,
  keyMapLighting,
  keyRgbItem,
  keyboardLightingCustomConf,
} from '../../../../types/keyboardLighting'
import { LIGHTING_TARGET, type KeyboardLightingTarget } from '../../../../types/lighting'
import { LightingDomainType, type LightingConfigData } from '../../../../types/lightingDomain'
import type { RGB, RGBGroup } from '../../../../types/color'
import { staticBacklightDomain, dynamicBacklightDomain } from '../../utils/domains/backlightDomain'
import { sideDomain } from '../../utils/domains/sideDomain'

export class GcomeApKeyboardLighting extends KeyboardLightingFeature {
  /** AP 单帧最大数据长度。 */
  private readonly frameLen = 56

  constructor(device: any) {
    super(device)
    LightingDomainRegistry.register(staticBacklightDomain)
    LightingDomainRegistry.register(dynamicBacklightDomain)
    LightingDomainRegistry.register(sideDomain)
  }

  // ==================== 基础读写（委托 lightingCapabilities） ====================

  /** 读取当前选中的板载配置分组（GetBase 偏移 0）。 */
  private async getActiveProfile(): Promise<number> {
    return capGetActiveProfile(this.device)
  }

  /** 分帧读取当前板载完整功能变量区（64 字节，地址叠加 profile 偏移）。 */
  private async readFuncBlock(profile?: number): Promise<Uint8Array> {
    return capReadFuncBlock(this.device, profile)
  }

  /** 读-改-写功能变量区，只写回发生变化的帧。 */
  private async mutateFuncBlock(
    mutator: (block: Uint8Array) => void | Promise<void>,
  ): Promise<void> {
    return capMutateFuncBlock(this.device, mutator)
  }

  /** 分帧读取当前板载的自定义灯光定义区（GetLedDefine）。 */
  private async readLedDefine(): Promise<Uint8Array> {
    return capReadLedDefine(this.device)
  }

  /** 分帧写回当前板载的自定义灯光定义区（SetLedDefine），跳过全 0 帧。
   *  previousState（修改前的 LED 状态）缺省时由底层自动读取固件当前状态。 */
  private async writeLedDefine(data: Uint8Array, previousState?: Uint8Array): Promise<void> {
    return capWriteLedDefine(this.device, data, previousState)
  }

  /**
   * 读取默认按键矩阵定义区（GetDefaultKeyMatrix）。
   *
   * 返回固件按键索引 0-127 与按键功能的对应关系（每键 3 字节：Type Code0 Code1）。
   * LED 定义区（GetLedDefine）的按键索引顺序与按键矩阵定义区一致，
   * 故可用本方法反查固件 LED 索引 → HID usage code 的真实映射。
   */
  async readDefaultKeyMatrix(): Promise<GcomeApKeyMatrixEntry[]> {
    const block = await capReadKeyMatrix(this.device)
    const entries: GcomeApKeyMatrixEntry[] = []

    for (let i = 0; i < KEY_COUNT; i++) {
      const base = i * 3
      if (base + 2 >= block.length) break
      entries.push({
        index: i,
        type: block[base],
        code0: block[base + 1],
        code1: block[base + 2],
      })
    }

    return entries
  }

  // ==================== 元数据 ====================

  getSupportedDomains(): LightingDomainType[] {
    return LightingDomainRegistry.getAllTypes()
  }

  async getEffects(
    target: KeyboardLightingTarget = LIGHTING_TARGET.Keyboard,
  ): Promise<KeyboardLightingEffect[]> {
    return shared.getEffects(this.device, gcomeSendRead, target)
  }

  // ==================== 背光 ====================

  async getCurrentEffect(): Promise<number> {
    const block = await this.readFuncBlock()
    return block[FUNC_OFFSET.lightMode]
  }

  async setCurrentEffect(effect: number): Promise<void> {
    await this.mutateFuncBlock(async (block) => {
      const prevMode = block[FUNC_OFFSET.lightMode]
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.backlight
      block[FUNC_OFFSET.lightMode] = effect & 0xff

      // 切到自定义逐键模式（GAME_MODE=0）＝进入临时灯效（音乐律动）：
      // 先把当前背光模式保存到 APP 数据区「背光/侧灯关闭前模式」bl（与背光关闭时的
      // 操作等价，复用同一槽位），退出时由 setBacklightSwitch(true) 从 bl 恢复，
      // 而不是固定回光谱循环。当前已是自定义模式时无需重复保存。
      if (effect === LIGHT_MODE.custom && prevMode !== LIGHT_MODE.custom) {
        const profile = await this.getActiveProfile()
        const state = await this.readLightState()
        state.backlight = ensureArrayLength(state.backlight, profile)
        state.backlight[profile] = prevMode
        await this.writeLightState(state)
      }
    })
  }

  async getBrightness(): Promise<number> {
    const block = await this.readFuncBlock()
    // 协议亮度 0-100 原样透出
    return block[FUNC_OFFSET.brightness]
  }

  async setBrightness(brightness: number): Promise<void> {
    await this.mutateFuncBlock((block) => {
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.backlight
      // 协议亮度 0-100 原样写入
      block[FUNC_OFFSET.brightness] = clampByte(brightness, 0, 100)
    })
  }

  async getSpeed(): Promise<number> {
    const block = await this.readFuncBlock()
    // 协议 0=最快、4=最慢，反转后返回 UI 档位语义（4=最快）
    return 4 - block[FUNC_OFFSET.speed]
  }

  async setSpeed(speed: number): Promise<void> {
    await this.mutateFuncBlock((block) => {
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.backlight
      // UI 档位反转：4(快) → 协议 0(快)；0(慢) → 协议 4(慢)
      block[FUNC_OFFSET.speed] = 4 - clampByte(speed, 0, 4)
    })
  }

  /**
   * 背光开关（0=关, 1=开）。
   * 关闭时把"当前板载的背光模式"保存到 APP 自行管理的数据区（JSON，按板载下标），
   * 再写入关闭编码 0xff；重新开启时从 APP 数据区恢复关闭前的灯效。
   * 开启时若当前正处于"临时占用"状态——熄灭（0xff）或自定义逐键模式
   * （GAME_MODE=0，音乐律动等临时灯效），一律从 bl 恢复占用前的模式；
   * 其余情况保持用户当前选择的灯效不变，仅清除槽位。
   */
  async setBacklightSwitch(enabled: boolean): Promise<void> {
    const profile = await this.getActiveProfile()
    await this.mutateFuncBlock(async (block) => {
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.backlight
      if (enabled) {
        const state = await this.readLightState()
        state.backlight = ensureArrayLength(state.backlight, profile)
        const prev = state.backlight[profile]
        if (
          block[FUNC_OFFSET.lightMode] === 0xff ||
          block[FUNC_OFFSET.lightMode] === LIGHT_MODE.custom
        ) {
          block[FUNC_OFFSET.lightMode] = prev === LIGHT_STATE_UNSET ? 1 : prev
        }
        state.backlight[profile] = LIGHT_STATE_UNSET
        await this.writeLightState(state)
      } else {
        const state = await this.readLightState()
        state.backlight = ensureArrayLength(state.backlight, profile)
        state.backlight[profile] = block[FUNC_OFFSET.lightMode]
        await this.writeLightState(state)
        block[FUNC_OFFSET.lightMode] = 0xff
      }
    })
  }

  async getBacklightSwitch(): Promise<number> {
    const mode = await this.getCurrentEffect()
    return mode === 0xff ? 0 : 1
  }

  // ==================== 侧灯 ====================

  /** 设置侧灯模式（侧灯专用编码 0-4：流光/七彩/呼吸/常亮/关闭）。 */
  async setSideEffect(effect: number): Promise<void> {
    await this.mutateFuncBlock((block) => {
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.side
      block[FUNC_OFFSET.sideMode] = effect & 0xff
    })
  }

  async getSideEffect(): Promise<number> {
    const block = await this.readFuncBlock()
    return block[FUNC_OFFSET.sideMode]
  }

  /**
   * 侧灯开关（0=关, 1=开）。
   * 关闭时把"当前板载的侧灯模式"保存到 APP 数据区（JSON，按板载下标），
   * 再写入侧灯关闭编码 4；重新开启时从 APP 数据区恢复关闭前的灯效。
   */
  async setSideLightDynamicEnable(enable: boolean): Promise<void> {
    const profile = await this.getActiveProfile()
    await this.mutateFuncBlock(async (block) => {
      block[FUNC_OFFSET.moduleSwitch] = LIGHT_MODULE.side
      if (enable) {
        const state = await this.readLightState()
        state.side = ensureArrayLength(state.side, profile)
        const prev = state.side[profile]
        block[FUNC_OFFSET.sideMode] =
          prev === LIGHT_STATE_UNSET || prev === SIDE_LIGHT_MODE.off ? SIDE_LIGHT_MODE.cycle : prev
        state.side[profile] = LIGHT_STATE_UNSET
        await this.writeLightState(state)
      } else {
        const state = await this.readLightState()
        state.side = ensureArrayLength(state.side, profile)
        state.side[profile] = block[FUNC_OFFSET.sideMode]
        await this.writeLightState(state)
        block[FUNC_OFFSET.sideMode] = SIDE_LIGHT_MODE.off
      }
    })
  }

  async getSideLightDynamicEnable(): Promise<boolean> {
    const mode = await this.getSideEffect()
    return mode !== SIDE_LIGHT_MODE.off
  }

  // ==================== 领域通用 CRUD（LightingDomainRegistry 驱动） ====================

  async getConfig(domain: LightingDomainType, index: number): Promise<LightingConfigData> {
    return shared.getConfig(this.device, gcomeSendRead, domain, index)
  }

  async setConfig(
    domain: LightingDomainType,
    index: number,
    config: Partial<LightingConfigData>,
  ): Promise<void> {
    return shared.setConfig(this.device, gcomeSendRead, gcomeSendWrite, domain, index, config)
  }

  async setProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
    value: number | boolean | RGB | RGBGroup,
  ): Promise<void> {
    // 键别名：'color'/'colorful' 为测试页使用的旧名，统一映射到领域 parser 字段
    const normalizedKey = key === 'color' ? 'rgb' : key === 'colorful' ? 'isColorful' : key
    return shared.setProperty(
      this.device,
      gcomeSendRead,
      gcomeSendWrite,
      domain,
      index,
      normalizedKey,
      value,
    )
  }

  async getProperty(
    domain: LightingDomainType,
    index: number,
    key: string,
  ): Promise<number | boolean | RGB | RGBGroup> {
    const normalizedKey = key === 'color' ? 'rgb' : key
    return shared.getProperty(this.device, gcomeSendRead, domain, index, normalizedKey)
  }

  // ==================== 逐键颜色（自定义灯光定义区） ====================

  async setKeysColor(
    _domain: LightingDomainType,
    keyMap: keyMapLighting,
    _index: number,
  ): Promise<void[]> {
    const led = await this.readLedDefine()
    // 保存修改前状态，供 writeLedDefine 差异比较（避免重复读取固件）
    const prev = led.slice()
    for (const item of keyMap) {
      const base = item.index * 3
      if (!item.rgb || !Number.isInteger(item.index) || base + 2 >= led.length) continue
      led[base] = clampByte(item.rgb.r, 0, 255)
      led[base + 1] = clampByte(item.rgb.g, 0, 255)
      led[base + 2] = clampByte(item.rgb.b, 0, 255)
    }
    await this.writeLedDefine(led, prev)
    return []
  }

  async getKeysColor(_domain: LightingDomainType, _index: number): Promise<keyMapLighting> {
    const led = await this.readLedDefine()
    const items: keyRgbItem[] = []
    const keyCount = Math.min(KEY_COUNT, Math.floor(led.length / 3))
    for (let i = 0; i < keyCount; i++) {
      items.push({
        index: i,
        rgb: { r: led[i * 3], g: led[i * 3 + 1], b: led[i * 3 + 2] },
      })
    }
    return items
  }

  async *getKeysColorStream(
    _domain: LightingDomainType,
    _index: number,
  ): AsyncIterable<keyRgbItem> {
    const led = await this.readLedDefine()
    const keyCount = Math.min(KEY_COUNT, Math.floor(led.length / 3))
    for (let i = 0; i < keyCount; i++) {
      yield {
        index: i,
        rgb: { r: led[i * 3], g: led[i * 3 + 1], b: led[i * 3 + 2] },
      }
    }
  }

  // ==================== 自定义灯效 ====================

  async createCustomEffect(_config: keyboardLightingCustomConf, _index: number): Promise<void> {
    return this.unsupported('GcomeApKeyboardLighting.createCustomEffect')
  }

  async deleteCustomEffect(_index: number): Promise<number> {
    return this.unsupported('GcomeApKeyboardLighting.deleteCustomEffect')
  }

  // ==================== APP 数据区状态持久化 ====================

  /**
   * 从 APP 自行管理的数据区读取"背光/侧灯关闭前模式"。
   * 数据为 JSON：`{"bl":[板载0,板载1,...],"sd":[板载0,板载1,...]}`，
   * 下标 = 板载 index；`0xff` 表示该板载未记录。
   */
  private async readLightState(): Promise<GcomeApLightState> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const chunks: Uint8Array[] = []

    for (let i = 0; i < 4; i++) {
      const address = LIGHT_STATE_APP_OFFSET + i * this.frameLen
      const res = await device.sendRawApCommand({
        command: GCOME_AP_CMD.getAppDefine,
        address,
        data: new Array(this.frameLen).fill(0),
      })
      const chunk = res.response.data
      chunks.push(chunk)
      if (chunk.every((b: number) => b === 0xff || b === 0x00)) break
      if (chunk.length < this.frameLen) break
    }

    const total = chunks.reduce((sum, c) => sum + c.length, 0)
    const buf = new Uint8Array(total)
    let offset = 0
    for (const chunk of chunks) {
      buf.set(chunk, offset)
      offset += chunk.length
    }

    // 截断 0xff/0x00 空填充后解析 JSON
    let text = ''
    for (const b of buf) {
      if (b === 0xff || b === 0x00) break
      text += String.fromCharCode(b)
    }
    if (!text.trim()) return { backlight: [], side: [] }
    try {
      const parsed = JSON.parse(text) as { bl?: number[]; sd?: number[] }
      return {
        backlight: Array.isArray(parsed?.bl) ? parsed.bl : [],
        side: Array.isArray(parsed?.sd) ? parsed.sd : [],
      }
    } catch {
      return { backlight: [], side: [] }
    }
  }

  /** 把"背光/侧灯关闭前模式"序列化为 JSON 并写入 APP 数据区。 */
  private async writeLightState(state: GcomeApLightState): Promise<void> {
    const device = this.device as unknown as GcomeApKeyboardDevice
    const json = JSON.stringify({ bl: state.backlight, sd: state.side })
    const bytes = utf8ToBytes(json)
    const totalFrames = Math.ceil(bytes.length / this.frameLen)

    for (let i = 0; i < totalFrames; i++) {
      const start = i * this.frameLen
      const end = Math.min(start + this.frameLen, bytes.length)
      const chunk = bytes.slice(start, end)
      await device.sendRawApCommand({
        command: GCOME_AP_CMD.setAppDefine,
        address: LIGHT_STATE_APP_OFFSET + start,
        data: Array.from(chunk),
      })
    }

    // 若新 JSON 较短，清除本帧剩余字节（0xff），避免旧 JSON 残留导致解析失败
    const remainder = bytes.length % this.frameLen
    if (remainder !== 0) {
      await device.sendRawApCommand({
        command: GCOME_AP_CMD.setAppDefine,
        address: LIGHT_STATE_APP_OFFSET + bytes.length,
        data: new Array(this.frameLen - remainder).fill(0xff),
      })
    }
  }
}

// ─── 工具函数 ───────────────────────────────────────────────────────────────

/** APP 数据区中保存的背光/侧灯关闭前模式（按板载下标）。 */
interface GcomeApLightState {
  /** 各板载背光关闭前模式（0-22），`LIGHT_STATE_UNSET`=未记录 */
  backlight: number[]
  /** 各板载侧灯关闭前模式（0-4），`LIGHT_STATE_UNSET`=未记录 */
  side: number[]
}

function clampByte(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value) || 0))
}

/** 确保数组长度覆盖到 index，不足位以 LIGHT_STATE_UNSET 补齐。 */
function ensureArrayLength(arr: number[], index: number): number[] {
  const result = arr.slice()
  while (result.length <= index) result.push(LIGHT_STATE_UNSET)
  return result
}

/** UTF-8 字符串 → 字节数组（JSON 为 ASCII，按字符码转字节）。 */
function utf8ToBytes(text: string): Uint8Array {
  return new Uint8Array(Array.from(text).map((c) => c.charCodeAt(0)))
}
